import { ApolloError, gql, NetworkStatus, useQuery } from "@apollo/client";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import useRetry from "../../useRetry";
import { safeJsonParse } from "../../../utils";

/**
 * A single scope row as returned (post-`JSON.parse`) by the `scopes_summary`
 * GraphQL field. The server hands back a JSON-encoded string, so these shapes
 * are not enforced by the GraphQL schema and are typed structurally here.
 */
export interface ScopeSummaryItem {
  id: string;
  total_count?: number;
  total_tickets?: number;
  count_per_severity?: Record<number, number>;
  per_ticket_status?: Record<string, number>;
  per_ticket_due_date_status?: Record<string, number>;
}

/**
 * An entry in the `scopesMap`. Seeded by the consumer (name/id/type/isLoading)
 * and enriched with `scopeData`/`pastDueCount` once the summary query resolves.
 */
export interface ScopeMapEntry {
  name?: string;
  id?: string;
  type?: string;
  isLoading: boolean;
  scopeData?: ScopeSummaryItem;
  pastDueCount?: number;
}

export type ScopesMap = Map<string | undefined, ScopeMapEntry>;

/** `scope_to_resource_count` (post-`JSON.parse`): scope id -> resource count. */
export type ResourceToScopeMap = Record<string, number>;

export interface UseScopesGraphOptions {
  scopes: (string | undefined)[] | undefined;
  filterToExecute?: string;
  isForExport?: boolean;
}

export interface UseScopesGraphReturn {
  scopesMap: ScopesMap;
  resourceToScopeMap: ResourceToScopeMap;
  maxNumOfFindings: number;
  maxNumOfTickets: number;
  setScopesMap: Dispatch<SetStateAction<ScopesMap>>;
  setMaxNumOfFindings: Dispatch<SetStateAction<number>>;
  setMaxNumOfTickets: Dispatch<SetStateAction<number>>;
  finalError: ApolloError | null;
  refetch: () => void;
  refetching: boolean;
  allPagesLoaded: boolean;
}

interface ScopesSummaryQueryData {
  scopes_summary: string;
}

interface ScopeToResourceCountQueryData {
  scope_to_resource_count: string;
}

const useScopesGraph = ({
  scopes,
  filterToExecute,
  isForExport,
}: UseScopesGraphOptions): UseScopesGraphReturn => {
  const pageSize = 3;
  const numOfPages = Math.ceil((scopes?.length ?? 0) / pageSize);

  const [currentPage, setCurrentPage] = useState(0);
  const [scopesMap, setScopesMap] = useState<ScopesMap>(new Map());
  const [maxNumOfFindings, setMaxNumOfFindings] = useState(0);
  const [maxNumOfTickets, setMaxNumOfTickets] = useState(0);
  const [resourceToScopeMap, setResourceToScopeMap] =
    useState<ResourceToScopeMap>({});
  const [allPagesLoaded, setAllPagesLoaded] = useState(false);

  const currentScopes = useMemo(() => {
    const newScopes = scopes?.slice(
      currentPage * pageSize,
      pageSize * (currentPage + 1)
    );
    return newScopes;
  }, [currentPage, scopes]);

  const filters_config = {
    filtersjson: filterToExecute,
  };

  const {
    data: resourcesCountData,
    refetch: refetchResourcesCount,
    networkStatus: resourcesCountNetworkStatus,
    error: resourcesCountError,
  } = useQuery<ScopeToResourceCountQueryData>(GET_SCOPES_RESOURCES_COUNT, {
    variables: { ScopeIds: scopes },
    skip: !scopes?.length,
  });

  const { data, error, refetch, networkStatus } =
    useQuery<ScopesSummaryQueryData>(GET_SCOPES_SUMMARY, {
      variables: { filters_config: filters_config, scope_ids: currentScopes },
      notifyOnNetworkStatusChange: true,
      skip: !currentScopes?.length || !filterToExecute,
    });

  const { finalError, resetRetries } = useRetry({
    requestError: error || resourcesCountError,
    refetch: () => {
      refetch();
      refetchResourcesCount();
    },
  });

  useEffect(() => {
    if (!!scopes?.length) {
      setMaxNumOfFindings(0);
      setMaxNumOfTickets(0);
      setCurrentPage(0);
      setAllPagesLoaded(false);
    }
  }, [scopes]);

  // The `data` reference this effect last consumed. Apollo returns the SAME
  // result object for deep-equal variables (its result cache), so a re-selected
  // scope set that is already cached produces no new reference — `data` identity
  // alone cannot tell us whether there is merging left to do.
  const lastMergedData = useRef<ScopesSummaryQueryData | null>(null);

  useEffect(() => {
    if (!data) {
      return;
    }

    const isNewData = lastMergedData.current !== data;
    lastMergedData.current = data;

    // `null`, not `[]`, so a malformed payload stays distinguishable from a
    // legitimately empty page — the two need different handling below.
    const parsed = safeJsonParse<ScopeSummaryItem[] | null>(
      data?.scopes_summary,
      null
    );
    const dataJson = parsed ?? [];

    // Merging on re-seeded-as-loading (not just on new data) is what makes this
    // effect safe to re-run on a `scopesMap` change: re-selecting an already
    // cached scope set re-seeds every row, and those rows would otherwise stay
    // stuck on their skeletons forever.
    const itemsToMerge = dataJson.filter((item) => {
      const dataItem = scopesMap.get(item?.id);
      return !!dataItem && (isNewData || dataItem.isLoading);
    });

    // A malformed payload yields nothing to merge, which would strand this
    // page's rows on their skeletons forever and hang the PDF export (it waits
    // on `allPagesLoaded`). Settle them data-less instead of hanging.
    const idsToSettle =
      parsed === null
        ? (currentScopes ?? []).filter((id) => scopesMap.get(id)?.isLoading)
        : [];

    let mergedMap = scopesMap;

    if (itemsToMerge.length || idsToSettle.length) {
      mergedMap = new Map(scopesMap);
      itemsToMerge.forEach((item) => {
        const dataItem = mergedMap.get(item?.id);
        if (!dataItem) {
          return;
        }
        // Replaced rather than mutated in place, so we never write through to
        // the entry objects still held by the previous state.
        mergedMap.set(item?.id, {
          ...dataItem,
          isLoading: false,
          scopeData: item,
          pastDueCount: item?.per_ticket_due_date_status?.PAST_DUE || 0,
        });
      });
      idsToSettle.forEach((id) => {
        const dataItem = mergedMap.get(id);
        if (dataItem) {
          mergedMap.set(id, { ...dataItem, isLoading: false });
        }
      });
      setScopesMap(mergedMap);
    }

    // The maxima accumulate across pages, so only a new page can move them.
    if (isNewData) {
      resetRetries();
      let maxFindings = maxNumOfFindings;
      let maxTickets = maxNumOfTickets;

      dataJson?.forEach((item) => {
        if (isForExport) {
          const maxInPriority = Math.max(
            ...Object.values(item?.count_per_severity || {})
          );
          if (maxInPriority > maxFindings) {
            maxFindings = maxInPriority;
          }
        } else if ((item.total_count ?? 0) > maxFindings) {
          maxFindings = item.total_count ?? 0;
        }

        if ((item.total_tickets ?? 0) > maxTickets) {
          maxTickets = item.total_tickets ?? 0;
        }
      });

      setMaxNumOfFindings(maxFindings);
      setMaxNumOfTickets(maxTickets);
    }

    if (currentPage < numOfPages - 1) {
      // Paging stays keyed strictly to a new result. Advancing on a re-merge of
      // already-consumed data would skip a page, leaving its rows unloaded.
      if (isNewData) {
        setCurrentPage(currentPage + 1);
      }
      return;
    }

    // Last page merged into scopesMap -> all rows are now renderable. Drives
    // export-readiness (see ExportedScopesGraph), so the PDF is only captured
    // once every scope row exists — hence also requiring no row still loading,
    // which stops a stale `data` marking the export ready early.
    const hasLoadingRows = Array.from(mergedMap.values()).some(
      (entry) => entry.isLoading
    );
    if (isNewData || !hasLoadingRows) {
      setAllPagesLoaded(true);
    }
  }, [data, scopesMap]);

  useEffect(() => {
    const resourcesCount = safeJsonParse<ResourceToScopeMap>(
      resourcesCountData?.scope_to_resource_count,
      {}
    );
    setResourceToScopeMap(resourcesCount);
  }, [resourcesCountData]);

  return {
    scopesMap,
    resourceToScopeMap,
    maxNumOfFindings,
    maxNumOfTickets,
    setScopesMap,
    setMaxNumOfFindings,
    setMaxNumOfTickets,
    finalError,
    refetch: () => {
      refetch();
      refetchResourcesCount();
    },
    refetching:
      networkStatus === NetworkStatus.refetch ||
      resourcesCountNetworkStatus === NetworkStatus.refetch,
    allPagesLoaded,
  };
};

export const GET_SCOPES_SUMMARY = gql`
  query getScopesSummary(
    $filters_config: FiltersConfigInput
    $scope_ids: [String]
  ) {
    scopes_summary(filters_config: $filters_config, scope_ids: $scope_ids)
  }
`;

export const GET_SCOPES_RESOURCES_COUNT = gql`
  query GetScopeToResourceCount($ScopeIds: [String]!) {
    scope_to_resource_count(scope_ids: $ScopeIds)
  }
`;

export default useScopesGraph;
